import React from 'react';
import { User, Mail, Phone, MapPin, Calendar, Shield, AlertTriangle, Building2, CheckCircle, XCircle } from 'lucide-react';
import { DeleteRequest } from '../types';
import { Modal } from './Modal';
import { Badge } from './Badge';
import { LoadingSpinner } from './LoadingSpinner';

interface AdminDeleteRequestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: DeleteRequest | null;
  loading?: boolean;
  onApprove?: () => void;
  onReject?: () => void;
}

export const AdminDeleteRequestDetailsModal: React.FC<AdminDeleteRequestDetailsModalProps> = ({
  isOpen,
  onClose,
  request,
  loading = false,
  onApprove,
  onReject,
}) => {
  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Loading Details..." size="xl">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </Modal>
    );
  }

  if (!request) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeColor = (status: string): 'warning' | 'success' | 'error' | 'primary' => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'primary';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Request Details" size="xl">
      <div className="space-y-6">
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-slate-900">Request Overview</h3>
            <Badge variant={getStatusBadgeColor(request.status)}>
              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-600">Request ID</p>
              <p className="text-base font-medium text-slate-900">#{request.requestId}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Account Type</p>
              <p className="text-base font-medium text-slate-900 flex items-center gap-2">
                {request.accountType === 'provider' ? (
                  <>
                    <Building2 className="h-4 w-4 text-green-600" />
                    Provider
                  </>
                ) : (
                  <>
                    <User className="h-4 w-4 text-blue-600" />
                    Service User
                  </>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Requested On</p>
              <p className="text-base font-medium text-slate-900 flex items-center gap-2">
                <Calendar className="h-4 w-4 text-slate-400" />
                {formatDate(request.createdAt)}
              </p>
            </div>
            {request.updatedAt && (
              <div>
                <p className="text-sm text-slate-600">Last Updated</p>
                <p className="text-base font-medium text-slate-900">
                  {formatDate(request.updatedAt)}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">User Information</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-600">Full Name</p>
                <p className="text-base font-medium text-slate-900">{request.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-slate-400" />
              <div>
                <p className="text-sm text-slate-600">Email Address</p>
                <p className="text-base font-medium text-slate-900">{request.email}</p>
              </div>
            </div>
            {request.profileSnapshot?.phone && (
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-600">Phone Number</p>
                  <p className="text-base font-medium text-slate-900">{request.profileSnapshot.phone}</p>
                </div>
              </div>
            )}
            {request.profileSnapshot?.address && (
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-600">Address</p>
                  <p className="text-base font-medium text-slate-900">
                    {request.profileSnapshot.address}
                    {request.profileSnapshot.city && `, ${request.profileSnapshot.city}`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {request.profileSnapshot && (
          <div className="border border-slate-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Account Statistics</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {request.profileSnapshot.registrationDate && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-600 mb-1">Registered</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {new Date(request.profileSnapshot.registrationDate).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </p>
                </div>
              )}
              {request.profileSnapshot.totalBookings !== undefined && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-xs text-blue-700 mb-1">Total Bookings</p>
                  <p className="text-sm font-semibold text-blue-900">{request.profileSnapshot.totalBookings}</p>
                </div>
              )}
              {request.profileSnapshot.activeBookings !== undefined && (
                <div className="bg-amber-50 p-3 rounded-lg">
                  <p className="text-xs text-amber-700 mb-1">Active Bookings</p>
                  <p className="text-sm font-semibold text-amber-900">{request.profileSnapshot.activeBookings}</p>
                </div>
              )}
              {request.profileSnapshot.totalReviews !== undefined && (
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-xs text-green-700 mb-1">Total Reviews</p>
                  <p className="text-sm font-semibold text-green-900">{request.profileSnapshot.totalReviews}</p>
                </div>
              )}
              {request.profileSnapshot.isVerified !== undefined && (
                <div className={`p-3 rounded-lg ${request.profileSnapshot.isVerified ? 'bg-green-50' : 'bg-slate-50'}`}>
                  <p className="text-xs text-slate-600 mb-1">Verification</p>
                  <p className="text-sm font-semibold flex items-center gap-1">
                    {request.profileSnapshot.isVerified ? (
                      <>
                        <Shield className="h-4 w-4 text-green-600" />
                        <span className="text-green-900">Verified</span>
                      </>
                    ) : (
                      <span className="text-slate-900">Not Verified</span>
                    )}
                  </p>
                </div>
              )}
            </div>
            {request.profileSnapshot.typeOfCare && request.profileSnapshot.typeOfCare.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-slate-600 mb-2">Services Offered</p>
                <div className="flex flex-wrap gap-2">
                  {request.profileSnapshot.typeOfCare.map((type, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full"
                    >
                      {type}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-amber-900 mb-3 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Deletion Reason
          </h3>
          <p className="text-slate-700">{request.reason || 'No reason provided'}</p>
        </div>

        {request.rejectionReason && (
          <div className="border border-red-200 bg-red-50 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center gap-2">
              <XCircle className="h-5 w-5" />
              Rejection Reason
            </h3>
            <p className="text-red-700">{request.rejectionReason}</p>
          </div>
        )}

        {request.status === 'approved' && (
          <div className="border border-green-200 bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-900">
              <CheckCircle className="h-5 w-5" />
              <p className="font-semibold">This account deletion request has been approved and processed.</p>
            </div>
          </div>
        )}

        {request.status === 'pending' && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <h4 className="text-sm font-semibold text-red-900 mb-2 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Warning
            </h4>
            <p className="text-sm text-red-700">
              Approving this request will permanently delete the user account, all associated data, and cannot be undone.
              Please review all information carefully before proceeding.
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          {request.status === 'pending' && onApprove && onReject && (
            <>
              <button
                onClick={onReject}
                className="px-6 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                Reject
              </button>
              <button
                onClick={onApprove}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Approve Deletion
              </button>
            </>
          )}
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};
